
import React, { useState } from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Search, GraduationCap, X } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { loadProfessions } from '@/services/professionService';
import { Profession } from '@/lib/professionTypes';
import { useQuery } from '@tanstack/react-query';
import ProfessionsList from './ProfessionsList';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface FilterProfessionsProps {
  selectedProfessions: Record<string, boolean>;
  onProfessionClick: (id: string) => void;
}

const FilterProfessions = ({ selectedProfessions, onProfessionClick }: FilterProfessionsProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  
  const { data: professions = [], isLoading } = useQuery({
    queryKey: ['professions'],
    queryFn: loadProfessions,
  });
  
  // Szűrt szakmák a keresés alapján
  const filteredProfessions = searchTerm 
    ? professions.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        p.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : professions;
  
  // Kiválasztott szakmák száma
  const selectedCount = Object.values(selectedProfessions).filter(Boolean).length;

  // Minden szakma kijelölés törlése
  const clearAllProfessions = () => {
    Object.keys(selectedProfessions).forEach(id => {
      if (selectedProfessions[id]) {
        onProfessionClick(id);
      }
    });
  };

  // A kiválasztott szakmák azonosítóinak listája
  const selectedProfessionIds = Object.entries(selectedProfessions)
    .filter(([_, selected]) => selected)
    .map(([id]) => id);
  
  // A kiválasztott szakmák adatai
  const selectedProfessionsData = selectedProfessionIds.map(id => 
    professions.find(p => p.id === id)
  ).filter(Boolean) as Profession[];
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-medium flex items-center gap-1.5">
          <GraduationCap className="h-4 w-4 text-muted-foreground" />
          Szakmák szerint
        </div>
        {selectedCount > 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 px-2 text-xs"
            onClick={clearAllProfessions}
          >
            Törlés ({selectedCount})
          </Button>
        )}
      </div>
      
      {/* Kiválasztott szakmák megjelenítése kártyákon */}
      {selectedCount > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedProfessionsData.slice(0, 5).map(profession => (
            <Badge 
              key={profession.id} 
              variant="secondary"
              className="flex items-center gap-1 px-2 py-1"
            >
              {profession.name}
              <button
                onClick={() => onProfessionClick(profession.id)}
                className="ml-1 rounded-full hover:bg-gray-200 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {selectedCount > 5 && (
            <Badge variant="outline" className="px-2 py-1">
              +{selectedCount - 5} további
            </Badge>
          )}
        </div>
      )}
      
      {/* Popover a szakmák kiválasztásához */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between border-dashed border-gray-300"
          >
            <span className="truncate">Szakmák kiválasztása</span>
            <Badge className="ml-2" variant="secondary">{selectedCount}</Badge>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[350px] p-4" align="start">
          <div className="space-y-4">
            <div className="font-medium flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <GraduationCap className="h-4 w-4" />
                Szakmák kiválasztása
              </div>
              {selectedCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs"
                  onClick={clearAllProfessions}
                >
                  Törlés ({selectedCount})
                </Button>
              )}
            </div>
            
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Keressen szakterületet..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="h-[300px] overflow-hidden">
              <ProfessionsList 
                isLoading={isLoading}
                filteredProfessions={filteredProfessions}
                groupedProfessions={
                  filteredProfessions.reduce<Record<string, Profession[]>>((groups, profession) => {
                    const category = profession.category;
                    if (!groups[category]) {
                      groups[category] = [];
                    }
                    groups[category].push(profession);
                    return groups;
                  }, {})
                }
                selectedProfessions={selectedProfessions}
                onProfessionClick={onProfessionClick}
              />
            </div>
            
            <div className="flex justify-end">
              <Button 
                size="sm" 
                onClick={() => setOpen(false)}
              >
                Kész
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      
      {/* Megtartjuk a ScrollArea-t is a szokásos szűrő funkcionalitásért */}
      <div className="md:hidden">
        <ScrollArea className="h-48 pr-2">
          <ProfessionsList 
            isLoading={isLoading}
            filteredProfessions={filteredProfessions}
            groupedProfessions={
              filteredProfessions.reduce<Record<string, Profession[]>>((groups, profession) => {
                const category = profession.category;
                if (!groups[category]) {
                  groups[category] = [];
                }
                groups[category].push(profession);
                return groups;
              }, {})
            }
            selectedProfessions={selectedProfessions}
            onProfessionClick={onProfessionClick}
          />
        </ScrollArea>
      </div>
    </div>
  );
};

export default FilterProfessions;
